import { useMemo } from 'react';
import { FORM_SECTIONS } from './formSchema.js';
import { buildEffectiveSections } from './fieldLayout.js';
import { buildPageGuide, buildWalkthroughSteps } from './walkthroughSteps.js';
import { useDoc } from './useFirestore.js';
import { useAuth } from '../auth/AuthProvider.jsx';
import { workflowEnabledFor } from './workflowAccess.js';
import { submitButtonLabel } from './matterRecordPdf.js';

/**
 * The walkthrough's steps for the form THIS firm is running.
 *
 * It reads `settings/fieldLayout` and merges it over the shipped schema — the same two inputs,
 * through the same function, that the intake form itself renders from. That is the whole point:
 * a tour built from the shipped schema alone was wrong on the day it was written (ten pages
 * hidden, four renamed, one added), and would drift again the next time somebody edited the form.
 *
 * Only the layout is read, not the whole `useFormConfig()` bundle. The steps describe pages, not
 * answers, so the roster, practice groups and derivation rules are eight subscriptions the tour
 * has no use for.
 *
 * `loading` exists so nothing is shown before the answer is in. Steps built from a missing layout
 * would be the shipped pages — the exact wrong list — and showing them for a moment and then
 * swapping them is worse than waiting.
 *
 * `guide` is the same pages as a written list, for the Help page, from the same subscription.
 */
export function useWalkthroughSteps() {
  const { data: fieldLayout, loading } = useDoc('settings/fieldLayout');
  // What happens at the end of the form is a setting, not a fact about the app: with the approval
  // workflow off a matter produces a PDF and goes nowhere. The closing steps read it rather than
  // asserting one or the other.
  const { data: general } = useDoc('settings/general');
  const { user } = useAuth();

  const sections = useMemo(
    () => (loading ? [] : buildEffectiveSections(FORM_SECTIONS, fieldLayout)),
    [fieldLayout, loading]
  );
  const options = useMemo(() => {
    const workflowEnabled = workflowEnabledFor(general, user?.uid);
    return { workflowEnabled, submitLabel: submitButtonLabel(general, { workflowEnabled }) };
  }, [general, user]);

  return {
    steps: useMemo(() => buildWalkthroughSteps(sections, options), [sections, options]),
    guide: useMemo(() => buildPageGuide(sections, options), [sections, options]),
    loading,
  };
}
